import { useEffect, useRef, useState } from 'react';

type ChainOwner = 'you' | 'radium';

export interface ChainNode {
  id: string;
  label: string;
  owner: ChainOwner;
  title: string;
  body: string;
  latency: string;
}

const CHAIN_NODES: ChainNode[] = [
  {
    id: 'signal',
    label: 'signal',
    owner: 'you',
    title: 'User signal',
    body: 'An impression, click, dwell, search, or navigation event from your app or site.',
    latency: '0ms',
  },
  {
    id: 'profile',
    label: 'profile',
    owner: 'you',
    title: 'Resolved profile',
    body: 'Your CDP / warehouse turns events into an enriched profile and audience membership.',
    latency: 'your stack',
  },
  {
    id: 'embedding',
    label: 'embedding',
    owner: 'radium',
    title: 'Content embedding',
    body: 'Radium has already vectorized every content unit and tagged it against your taxonomy.',
    latency: 'pre-computed',
  },
  {
    id: 'rank',
    label: 'rank',
    owner: 'radium',
    title: 'In-session rank',
    body: 'A fine-tuned reranker scores candidates against the live profile. Reasoning is logged.',
    latency: 'P95 < 200ms',
  },
  {
    id: 'impression',
    label: 'impression',
    owner: 'you',
    title: 'Surface render',
    body: 'Radium returns ranked, sequenced units. Your app or site renders them on the surface you own.',
    latency: 'your runtime',
  },
  {
    id: 'engagement',
    label: 'engagement',
    owner: 'you',
    title: 'Engagement',
    body: 'The user reacts. Click, scroll, dismiss, dwell — your instrumentation captures it.',
    latency: 'your runtime',
  },
  {
    id: 'next',
    label: 'next',
    owner: 'radium',
    title: 'Loop',
    body: 'The signal flows back. Models update. The next ranking incorporates this turn within seconds.',
    latency: 'real-time',
  },
];

const lanes: {
  id: string;
  label: string;
  from: number;
  to: number;
  owner: ChainOwner;
}[] = [
  { id: 'lane-1', label: 'Your data', from: 0, to: 1, owner: 'you' },
  { id: 'lane-2', label: 'Radium runtime', from: 2, to: 3, owner: 'radium' },
  { id: 'lane-3', label: 'Your surface', from: 4, to: 5, owner: 'you' },
  { id: 'lane-4', label: 'Loop', from: 6, to: 6, owner: 'radium' },
];

const ownerColor: Record<ChainOwner, string> = {
  you: 'var(--gray-300)',
  radium: 'var(--accent)',
};

const ownerBg: Record<ChainOwner, string> = {
  you: 'rgba(255,255,255,0.025)',
  radium: 'rgba(0,218,165,0.06)',
};

interface ChainVisualProps {
  activeForce?: number | null;
  onNodeFocus?: (node: ChainNode) => void;
}

export function ChainVisual({
  activeForce = null,
  onNodeFocus,
}: ChainVisualProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [focused, setFocused] = useState(0);
  const [paused, setPaused] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const userPickedRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRevealed(true);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!revealed || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setProgress(CHAIN_NODES.length);
      return;
    }
    const t = window.setInterval(() => {
      setProgress((p) => (p + 1) % (CHAIN_NODES.length + 2));
    }, 900);
    return () => window.clearInterval(t);
  }, [revealed, paused]);

  useEffect(() => {
    if (Date.now() - userPickedRef.current < 4000) return;
    if (progress >= 1 && progress <= CHAIN_NODES.length) {
      setFocused(progress - 1);
    }
  }, [progress]);

  useEffect(() => {
    if (activeForce == null) return;
    const map: Record<number, number> = { 0: 1, 1: 3, 2: 4 };
    const idx = map[activeForce];
    if (idx != null) {
      setFocused(idx);
      userPickedRef.current = Date.now();
    }
  }, [activeForce]);

  useEffect(() => {
    onNodeFocus?.(CHAIN_NODES[focused]);
  }, [focused, onNodeFocus]);

  const n = CHAIN_NODES.length;
  const litWidthPct =
    n > 1 && progress > 0 && progress <= n
      ? ((Math.min(progress, n) - 1) / (n - 1)) * (100 - 100 / n)
      : 0;

  const focusedNode = CHAIN_NODES[focused] ?? CHAIN_NODES[0];

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <div
        style={{ position: 'relative', overflowX: 'auto', paddingBottom: 4 }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${n}, minmax(72px, 1fr))`,
            gap: 0,
            minWidth: 560,
            position: 'relative',
          }}
        >
          {lanes.map((lane) => (
            <div
              key={lane.id}
              aria-hidden="true"
              style={{
                gridColumn: `${lane.from + 1} / ${lane.to + 2}`,
                gridRow: 1,
                background: ownerBg[lane.owner],
                border: `1px solid ${lane.owner === 'radium' ? 'rgba(0,218,165,0.22)' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: 8,
                padding: '0 12px',
                margin: '0 3px',
                height: 32,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition:
                  'background 320ms var(--ease-out), border-color 320ms var(--ease-out)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: ownerColor[lane.owner],
                  boxShadow:
                    lane.owner === 'radium'
                      ? '0 0 8px var(--radium-glow)'
                      : 'none',
                  flexShrink: 0,
                }}
              />
              <span
                className="text-mono"
                style={{
                  fontSize: 9.5,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color:
                    lane.owner === 'radium'
                      ? 'var(--accent)'
                      : 'var(--ink-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {lane.label}
              </span>
            </div>
          ))}
        </div>

        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${n}, minmax(72px, 1fr))`,
            gap: 0,
            minWidth: 560,
            marginTop: 20,
            position: 'relative',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 22,
              left: `${100 / n / 2}%`,
              right: `${100 / n / 2}%`,
              height: 1,
              background: 'var(--border-default)',
              zIndex: 0,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 22,
              left: `${100 / n / 2}%`,
              width: `calc(${litWidthPct}% )`,
              height: 1,
              background:
                'linear-gradient(90deg, transparent 0%, var(--accent) 30%, var(--accent) 100%)',
              boxShadow: '0 0 8px var(--radium-glow)',
              transition: 'width 800ms var(--ease-out)',
              zIndex: 0,
              opacity: progress > 0 && progress <= n ? 1 : 0,
            }}
          />
          {revealed && progress > 0 && progress <= n && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 22,
                left: `calc(${100 / n / 2}% + ${((progress - 1) / (n - 1)) * (100 - 100 / n)}%)`,
                transform: 'translate(-50%, -50%)',
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: 'var(--accent)',
                boxShadow:
                  '0 0 16px var(--accent), 0 0 32px var(--radium-glow)',
                transition: 'left 800ms var(--ease-out)',
                zIndex: 2,
              }}
            />
          )}

          {CHAIN_NODES.map((node, i) => {
            const lit = i < progress;
            const isFocused = i === focused;
            const isRadium = node.owner === 'radium';
            return (
              <button
                key={node.id}
                type="button"
                onClick={() => {
                  setFocused(i);
                  userPickedRef.current = Date.now();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '0 6px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  position: 'relative',
                  zIndex: 1,
                }}
                aria-label={`${node.title}: ${node.body}`}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    border: `1.5px solid ${isFocused ? 'var(--accent)' : lit ? (isRadium ? 'var(--accent)' : 'var(--ink-secondary)') : 'var(--border-default)'}`,
                    background: isFocused
                      ? isRadium
                        ? 'rgba(0,218,165,0.18)'
                        : 'rgba(255,255,255,0.08)'
                      : lit
                        ? isRadium
                          ? 'rgba(0,218,165,0.10)'
                          : 'rgba(255,255,255,0.04)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
                    backdropFilter: 'blur(8px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isFocused
                      ? '0 0 0 4px rgba(0,218,165,0.12), 0 0 32px var(--radium-glow)'
                      : lit && isRadium
                        ? '0 0 16px var(--radium-glow)'
                        : 'none',
                    transform: isFocused ? 'scale(1)' : 'scale(0.833)',
                    transition: 'transform 320ms var(--ease-out), border-color 320ms var(--ease-out), background 320ms var(--ease-out), box-shadow 320ms var(--ease-out)',
                  }}
                >
                  <div
                    style={{
                      width: isFocused ? 14 : 10,
                      height: isFocused ? 14 : 10,
                      borderRadius: '50%',
                      background: lit
                        ? isRadium
                          ? 'var(--accent)'
                          : 'var(--ink-primary)'
                        : 'var(--gray-500)',
                      transition: 'all 320ms var(--ease-out)',
                    }}
                  />
                </div>
                <div
                  className="text-mono"
                  style={{
                    fontSize: 11,
                    color: isFocused
                      ? isRadium
                        ? 'var(--accent)'
                        : 'var(--ink-primary)'
                      : lit
                        ? 'var(--ink-primary)'
                        : 'var(--ink-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    whiteSpace: 'nowrap',
                    transition: 'color 320ms var(--ease-out)',
                  }}
                >
                  {node.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="glass-panel-inset"
        style={{
          marginTop: 24,
          padding: '20px 22px',
          borderRadius: 12,
          minHeight: 132,
          border: `1px solid ${focusedNode.owner === 'radium' ? 'rgba(0,218,165,0.28)' : 'rgba(255, 255, 255, 0.1)'}`,
          background:
            focusedNode.owner === 'radium'
              ? 'linear-gradient(135deg, rgba(0,218,165,0.11) 0%, rgba(255,255,255,0.04) 55%, rgba(0,0,0,0.08) 100%)'
              : undefined,
          transition:
            'border-color 320ms var(--ease-out), background 320ms var(--ease-out)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 8,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              className="text-mono"
              style={{
                fontSize: 10,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color:
                  focusedNode.owner === 'radium'
                    ? 'var(--accent)'
                    : 'var(--ink-muted)',
                padding: '3px 8px',
                borderRadius: 4,
                border: `1px solid ${focusedNode.owner === 'radium' ? 'rgba(0,218,165,0.4)' : 'var(--border-default)'}`,
                background:
                  focusedNode.owner === 'radium'
                    ? 'rgba(0,218,165,0.08)'
                    : 'transparent',
              }}
            >
              {focusedNode.owner === 'radium' ? 'Radium' : 'You own'}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: 16,
                color: 'var(--ink-primary)',
              }}
            >
              {focusedNode.title}
            </span>
          </div>
          <span
            className="text-mono"
            style={{
              fontSize: 11,
              color: 'var(--ink-muted)',
              letterSpacing: '0.04em',
            }}
          >
            {focusedNode.latency}
          </span>
        </div>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--ink-secondary)',
            maxWidth: 720,
          }}
        >
          {focusedNode.body}
        </p>
      </div>

      <div
        className="text-mono"
        style={{
          marginTop: 12,
          fontSize: 11,
          color: 'var(--ink-muted)',
          letterSpacing: '0.04em',
          height: 16,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {paused
          ? 'Paused — click a node to inspect'
          : 'Auto-cycling — hover to pause'}
      </div>
    </div>
  );
}
